import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs/esm';

import { isPresent } from 'app/core/util/operators';
import { ApplicationConfigService } from 'app/core/config/application-config.service';
import { createRequestOption } from 'app/core/request/request-util';
import { ICbicMenuMst, getCbicMenuMstIdentifier } from '../cbic-menu-mst.model';
import { SessionStorageService } from 'ngx-webstorage';

export type EntityResponseType = HttpResponse<ICbicMenuMst>;
export type EntityArrayResponseType = HttpResponse<ICbicMenuMst[]>;

@Injectable({ providedIn: 'root' })
export class CbicMenuMstService {
  selectedMenu = new Subject<any>();
  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/cbic-menu-msts');
  protected menuUrl = this.applicationConfigService.getEndpointFor('api/cbicmenumsts');

  constructor(protected http: HttpClient,
     protected applicationConfigService: ApplicationConfigService,
     private sessionStorageService: SessionStorageService) {
        // this.sessionStorageService.store('selectedMenu', this.getSelectedMenu());
        this.selectedMenu.subscribe(menuItem=>{
            this.sessionStorageService.store('selectedMenu', menuItem);
        });
  }
  getSelectedMenu(): any {
    const selectedMenu = this.sessionStorageService.retrieve('selectedMenu');
    return selectedMenu ?? {id: 1};
  }
  updateSelectedMenu(selectedMenu: any) {
    this.selectedMenu.next(selectedMenu);
  }
  findByIsActive(): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicMenuMst[]>(`${this.menuUrl}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  create(cbicMenuMst: ICbicMenuMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicMenuMst);
    return this.http
      .post<ICbicMenuMst>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(cbicMenuMst: ICbicMenuMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicMenuMst);
    return this.http
      .put<ICbicMenuMst>(`${this.resourceUrl}/${getCbicMenuMstIdentifier(cbicMenuMst) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(cbicMenuMst: ICbicMenuMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicMenuMst);
    return this.http
      .patch<ICbicMenuMst>(`${this.resourceUrl}/${getCbicMenuMstIdentifier(cbicMenuMst) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<ICbicMenuMst>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicMenuMst[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addCbicMenuMstToCollectionIfMissing(
    cbicMenuMstCollection: ICbicMenuMst[],
    ...cbicMenuMstsToCheck: (ICbicMenuMst | null | undefined)[]
  ): ICbicMenuMst[] {
    const cbicMenuMsts: ICbicMenuMst[] = cbicMenuMstsToCheck.filter(isPresent);
    if (cbicMenuMsts.length > 0) {
      const cbicMenuMstCollectionIdentifiers = cbicMenuMstCollection.map(cbicMenuMstItem => getCbicMenuMstIdentifier(cbicMenuMstItem)!);
      const cbicMenuMstsToAdd = cbicMenuMsts.filter(cbicMenuMstItem => {
        const cbicMenuMstIdentifier = getCbicMenuMstIdentifier(cbicMenuMstItem);
        if (cbicMenuMstIdentifier == null || cbicMenuMstCollectionIdentifiers.includes(cbicMenuMstIdentifier)) {
          return false;
        }
        cbicMenuMstCollectionIdentifiers.push(cbicMenuMstIdentifier);
        return true;
      });
      return [...cbicMenuMstsToAdd, ...cbicMenuMstCollection];
    }
    return cbicMenuMstCollection;
  }

  protected convertDateFromClient(cbicMenuMst: ICbicMenuMst): ICbicMenuMst {
    return Object.assign({}, cbicMenuMst, {
      createdDt: cbicMenuMst.createdDt?.isValid() ? cbicMenuMst.createdDt.toJSON() : undefined,
      updatedDt: cbicMenuMst.updatedDt?.isValid() ? cbicMenuMst.updatedDt.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.createdDt = res.body.createdDt ? dayjs(res.body.createdDt) : undefined;
      res.body.updatedDt = res.body.updatedDt ? dayjs(res.body.updatedDt) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((cbicMenuMst: ICbicMenuMst) => {
        cbicMenuMst.createdDt = cbicMenuMst.createdDt ? dayjs(cbicMenuMst.createdDt) : undefined;
        cbicMenuMst.updatedDt = cbicMenuMst.updatedDt ? dayjs(cbicMenuMst.updatedDt) : undefined;
      });
    }
    return res;
  }  
}
